import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Calendar } from "lucide-react";
import { insights, findInsight } from "@/data/insights";
import Reveal from "@/components/site/Reveal";

export default function Insights() {
  return (
    <>
      {/* HEADER */}
      <section data-testid="insights-header" className="page-offset bg-[#F8F9FA] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.35] pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(10,31,68,0.08) 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <Reveal>
            <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
              Insights &amp; Resources
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 font-display font-light text-[#0A1F44] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Perspectives on tax,
              <br className="hidden md:block" /> <span className="italic">finance &amp; compliance.</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <div className="mt-8 h-[2px] w-14 bg-[#D4AF37] mx-auto" />
            <p className="mt-6 text-[15px] md:text-base leading-relaxed text-[#4B5563] max-w-2xl mx-auto">
              Short, practical reads from our practice — built from real mandates across GST,
              income tax, accounting and project finance.
            </p>
          </Reveal>
        </div>
      </section>

      {/* GRID */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 md:gap-10">
          {insights.map((post, i) => (
            <Reveal key={post.slug} delay={i * 90}>
              <article
                data-testid={`insight-card-${i}`}
                className="group bg-white border border-gray-200 overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_80px_-30px_rgba(10,31,68,0.25)] hover:border-[#D4AF37] h-full flex flex-col"
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-[#0A1F44]">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-70 group-hover:scale-[1.04] transition-all duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44]/50 to-transparent" />
                  <span className="absolute top-4 left-4 inline-flex items-center bg-[#D4AF37] text-[#0A1F44] text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5">
                    {post.category}
                  </span>
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <div className="flex items-center gap-5 text-[11px] uppercase tracking-[0.22em] text-[#4B5563]">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={12} strokeWidth={1.75} /> {post.date}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock size={12} strokeWidth={1.75} /> {post.readTime}
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-xl md:text-2xl text-[#0A1F44] font-medium leading-tight group-hover:text-[#0A1F44]">
                    {post.title}
                  </h3>
                  <p className="mt-4 text-[14px] text-[#4B5563] leading-relaxed flex-1">
                    {post.excerpt}
                  </p>
                  <Link
                    to={`/insights/${post.slug}`}
                    data-testid={`insight-read-${i}`}
                    className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#0A1F44] group-hover:text-[#D4AF37] transition-colors"
                  >
                    Read article
                    <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}

export function InsightDetail() {
  const { slug } = useParams();
  const post = findInsight(slug);

  if (!post) {
    return (
      <section className="page-offset py-28 text-center">
        <p className="text-[#4B5563]">Article not found.</p>
        <Link
          to="/insights"
          className="mt-6 inline-flex items-center gap-2 text-[#0A1F44] hover:text-[#D4AF37] uppercase tracking-[0.2em] text-xs"
        >
          <ArrowLeft size={14} /> Back to Insights
        </Link>
      </section>
    );
  }

  const related = insights.filter((i) => i.slug !== post.slug).slice(0, 3);

  return (
    <>
      <article className="page-offset bg-white">
        <div className="relative bg-[#0A1F44] text-white overflow-hidden">
          <img
            src={post.image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-[0.25]"
            aria-hidden="true"
          />
          <div className="relative max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <Reveal>
              <Link
                to="/insights"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold"
              >
                <ArrowLeft size={14} /> Back to Insights
              </Link>
            </Reveal>
            <Reveal delay={80}>
              <div className="mt-6 flex items-center gap-5 text-[11px] uppercase tracking-[0.22em] text-white/60">
                <span className="inline-flex items-center bg-[#D4AF37]/90 text-[#0A1F44] font-bold px-3 py-1">
                  {post.category}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={12} strokeWidth={1.75} /> {post.date}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={12} strokeWidth={1.75} /> {post.readTime}
                </span>
              </div>
            </Reveal>
            <Reveal delay={160}>
              <h1 className="mt-6 font-display font-light text-4xl md:text-5xl leading-[1.1] tracking-tight">
                {post.title}
              </h1>
            </Reveal>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
          {post.body.map((para, i) => (
            <Reveal key={i} delay={i * 70}>
              <p className="text-[16px] md:text-[17px] leading-[1.8] text-[#111827] mb-6">
                {para}
              </p>
            </Reveal>
          ))}

          <div className="mt-12 border-t border-gray-200 pt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <p className="text-[13px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">
              Need help on this topic?
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 bg-[#0A1F44] text-white px-7 py-3.5 font-semibold text-[13px] uppercase tracking-[0.14em] hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-colors"
            >
              Talk to our team <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </article>

      {/* RELATED */}
      <section className="py-20 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between">
            <h3 className="font-display text-2xl md:text-3xl text-[#0A1F44]">More Insights</h3>
            <Link
              to="/insights"
              className="hidden md:inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#0A1F44] hover:text-[#D4AF37]"
            >
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/insights/${p.slug}`}
                className="group bg-white border border-gray-200 p-6 hover:border-[#D4AF37] hover:-translate-y-1 transition-all duration-500"
              >
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  {p.category}
                </span>
                <h4 className="mt-3 font-display text-lg text-[#0A1F44] font-medium leading-tight">
                  {p.title}
                </h4>
                <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#0A1F44] group-hover:text-[#D4AF37]">
                  Read <ArrowRight size={12} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
